<template>
  <section class="section-base form-section-base">
    <v-container>
      <div class="form-base">
        <div class="form-base__title">
          В соответствии со статьей 103 Основ законодательства Российской
          Федерации о нотариате от 11.02.1993 № 4462-1 прошу
        </div>
        <div class="form-section-base__controls">
          <v-radio-group
            v-model="notice"
            :error-messages="getValidationErrorText($v.notice)"
          >
            <v-radio
              class="mt-2 mb-2"
              label="Для выполнения процессуальных действий по обеспечению доказательств направить извещение следующим сторонам/заинтересованным лицам (если таких лиц несколько, раздел заполняется в отношении каждого)"
              :value="true"
            />

            <div v-if="notice" class="pb-8 px-3">
              <Notificant
                v-for="(v, index) in $v.notificants.$each.$iter"
                :key="index"
                :v="v"
                :can-delete="notificants.length > 1"
                @delete="deleteNotificant(index)"
              />

              <v-btn
                depressed
                color="primary"
                class="button-base"
                @click="addNotificant"
              >
                <v-icon size="20" left>{{ mdiAccountMultiplePlus }}</v-icon>
                Добавить заинтересованное лицо
              </v-btn>
            </div>

            <v-radio
              class="mb-2"
              label="Обеспечить доказательства без извещения сторон и заинтересованных лиц"
              :value="false"
            />
          </v-radio-group>
        </div>

        <v-container v-if="notice === false">
          <v-radio-group
            v-model="noSubject"
            class="mt-0"
            :error-messages="getValidationErrorText($v.noSubject)"
          >
            <v-radio
              label="В связи со случаем, не терпящим отлагательства"
              :value="NoSubjectValue.NO_SUBJECT_URGENT_CASE"
            />
            <v-radio
              label="В связи с тем, что нельзя определить, кто впоследствии будет участвовать в деле"
              :value="NoSubjectValue.NO_SUBJECT_UNKNOWN_SUBJECTS_CASE"
            />
          </v-radio-group>
        </v-container>
      </div>

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { mdiAccountMultiplePlus } from '@mdi/js';
import { required } from 'vuelidate/lib/validators';

import { checkInvalid, leaveStep } from '@/utils/services';
import { getValidationErrorText } from '@/utils/validate';
import { Role } from '@/utils/dictionaries';
import { ServicesModule } from '@/store/modules/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import Notificant, {
  notificantModel,
  notificantValidation,
} from './Notificant';
import { SecuringEvidenceStep } from '@/constants';

const NoSubjectValue = {
  NO_SUBJECT_URGENT_CASE: 'noSubjectUrgentCase',
  NO_SUBJECT_UNKNOWN_SUBJECTS_CASE: 'noSubjectUnknownSubjectsCase',
};

export default {
  name: 'Notificants',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
  },
  components: { Notificant, Footer },
  data: () => ({
    StepDirection,
    StepName,
    leaveStep,
    Role,
    NoSubjectValue,
    mdiAccountMultiplePlus,
    getValidationErrorText,
    invalidFields: [],
    notice: null,
    notificants: [cloneDeep(notificantModel)],
    noSubject: null,
  }),
  validations: {
    notificants: {
      $each: {
        ...notificantValidation,
      },
    },
    notice: {
      required,
    },
    noSubject: {
      required,
    },
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      if (ServicesModule.statement.subjects.notificants?.length) {
        this.notice = true;
        this.notificants = ServicesModule.statement.subjects.notificants.map(
          (notificant) => {
            const resNotificant = cloneDeep(notificant);

            if (notificant[Role.PERSON]) {
              resNotificant.role = Role.PERSON;
            }

            if (notificant[Role.ORGANIZATION]) {
              resNotificant.role = Role.ORGANIZATION;
            }

            if (notificant[Role.FOREIGN_ORGANIZATION]) {
              resNotificant.role = Role.FOREIGN_ORGANIZATION;
            }

            return deepmerge(notificantModel, resNotificant);
          }
        );
      }

      if (
        ServicesModule.statement.securityEvidenceDetails?.noSubjectUrgentCase ||
        ServicesModule.statement.securityEvidenceDetails
          ?.noSubjectUnknownSubjectsCase
      ) {
        this.notice = false;

        if (
          ServicesModule.statement.securityEvidenceDetails.noSubjectUrgentCase
        ) {
          this.noSubject = NoSubjectValue.NO_SUBJECT_URGENT_CASE;
        }

        if (
          ServicesModule.statement.securityEvidenceDetails
            .noSubjectUnknownSubjectsCase
        ) {
          this.noSubject = NoSubjectValue.NO_SUBJECT_UNKNOWN_SUBJECTS_CASE;
        }
      }
    },

    addNotificant() {
      this.notificants.push(cloneDeep(notificantModel));
    },

    deleteNotificant(index) {
      this.notificants = this.notificants.filter((_, i) => i !== Number(index));
    },

    touchFields() {
      this.invalidFields = [];

      this.$v.notice.$touch();
      checkInvalid(this.$v.notice, this.invalidFields);

      if (this.notice) {
        for (const notificant of Object.values(
          this.$v.notificants.$each.$iter
        )) {
          notificant[notificant.role.$model].$touch();
          checkInvalid(notificant[notificant.role.$model], this.invalidFields);
        }
      }

      if (this.notice === false) {
        this.$v.noSubject.$touch();
        checkInvalid(this.$v.noSubject, this.invalidFields);
      }
    },

    assemblyModel() {
      const model = {
        notificants: null,
        securityEvidenceDetails: {
          noSubjectUrgentCase: false,
          noSubjectUnknownSubjectsCase: false,
        },
      };

      if (this.notice) {
        model.notificants = this.notificants.map((notificant) => {
          const resNotificant = cloneDeep(notificant);

          return {
            [resNotificant.role]: resNotificant[resNotificant.role],
          };
        });
      } else {
        model.securityEvidenceDetails = {
          ...model.securityEvidenceDetails,
          [this.noSubject]: true,
        };
      }

      return model;
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: SecuringEvidenceStep.NOTIFICANTS,
      });
    },
  },
};
</script>
